/**
 * SubgraphLayoutControls — shared layout direction + algorithm dropdown
 * for ImportFocusedSubgraph and FocusedSubgraph (call graph).
 * Issue #601: Add layout controls modal to subgraphs.
 */

import { useState, useEffect, useRef } from 'react';
import type { LayoutDirection, LayoutAlgorithm } from './layoutEngine';

const ALGORITHMS: { value: LayoutAlgorithm; label: string }[] = [
  { value: 'dagre', label: 'Hierarchical' },
  { value: 'elk',   label: 'Architecture' },
  { value: 'force', label: 'Cluster' },
  { value: 'radial',label: 'Radial' },
  { value: 'tree',  label: 'Tree' },
];

const DIR_TITLES: Record<LayoutDirection, string> = {
  TB: 'Top → Bottom',
  LR: 'Left → Right',
  BT: 'Bottom → Top',
  RL: 'Right → Left',
};

interface SubgraphLayoutControlsProps {
  layoutDirection: LayoutDirection;
  layoutAlgorithm: LayoutAlgorithm;
  onDirectionChange: (dir: LayoutDirection) => void;
  onAlgorithmChange: (algo: LayoutAlgorithm) => void;
  isLayouting?: boolean;
}

export function SubgraphLayoutControls({
  layoutDirection,
  layoutAlgorithm,
  onDirectionChange,
  onAlgorithmChange,
  isLayouting,
}: SubgraphLayoutControlsProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm flex items-center gap-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white ${isLayouting ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={isLayouting}
        title="Layout controls"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {isLayouting ? 'Layouting…' : 'Layout'}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 w-56">
          {/* Direction */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
              Direction
            </label>
            <div className="flex gap-1">
              {(['TB', 'LR', 'BT', 'RL'] as LayoutDirection[]).map(dir => (
                <button
                  key={dir}
                  onClick={() => { onDirectionChange(dir); setOpen(false); }}
                  className={`flex-1 py-1 text-xs rounded font-medium ${
                    layoutDirection === dir
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  title={DIR_TITLES[dir]}
                >
                  {dir}
                </button>
              ))}
            </div>
          </div>

          {/* Algorithm */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
              Algorithm
            </label>
            <div className="flex flex-col gap-1">
              {ALGORITHMS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => { onAlgorithmChange(value); setOpen(false); }}
                  className={`px-2 py-1 text-xs rounded text-left ${
                    layoutAlgorithm === value
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
